import { useQuery } from '@tanstack/react-query'
import { Link, createFileRoute } from '@tanstack/react-router'
import { CircleCheckBig, ClipboardList, CloudOff, FilePen, Plus, Zap } from 'lucide-react'
import { useMemo, useState } from 'react'
import { myReluxesQuery } from '@/api/queries'
import { WaitingToSend } from '@/components/app/waiting-to-send'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { Input } from '@/components/ui/field'
import { useMyDrafts } from '@/features/relux/drafts'
import { ReluxCard } from '@/features/relux/view/relux-card'
import { formatWhen } from '@/lib/format'
import { useOnline, useOutbox } from '@/offline/hooks'

export const Route = createFileRoute('/_app/reluxes/')({
  validateSearch: (s: Record<string, unknown>): { sent?: string } => ({
    sent: typeof s.sent === 'string' ? s.sent : undefined,
  }),
  component: MyReluxes,
})

function MyReluxes() {
  const { me } = Route.useRouteContext()
  const { sent } = Route.useSearch()
  const online = useOnline()
  const outbox = useOutbox(me.real_user_id)
  const drafts = useMyDrafts(me.real_user_id).filter((d) => d.status !== 'queued')
  const { data, isLoading } = useQuery(myReluxesQuery)
  const [q, setQ] = useState('')
  const stillWaiting = sent && outbox.some((i) => i.id === sent)

  const groups = useMemo(() => {
    const list = (data ?? []).filter(
      (r) =>
        !q ||
        `${r.project_name} ${r.ref} ${r.site_suburb ?? ''}`.toLowerCase().includes(q.toLowerCase()),
    )
    return {
      needsYou: list.filter((r) => r.status === 'info_needed'),
      ready: list.filter((r) => r.status === 'completed'),
      queue: list.filter((r) => r.status === 'submitted' || r.status === 'in_progress'),
      done: list.filter((r) => r.status === 'closed' || r.status === 'cancelled'),
    }
  }, [data, q])

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-3xl uppercase">My Reluxes</h1>
        <div className="flex gap-2">
          <Button asChild variant="outline">
            <Link to="/reluxes/new" search={{ quick: true }}>
              <Zap aria-hidden /> Quick
            </Link>
          </Button>
          <Button asChild>
            <Link to="/reluxes/new">
              <Plus aria-hidden /> New Relux
            </Link>
          </Button>
        </div>
      </div>

      {sent && (
        <div
          role="status"
          className={
            stillWaiting
              ? 'rounded-card bg-warning-bg p-4 text-warning-fg'
              : 'rounded-card bg-success-bg p-4 text-success-fg'
          }
        >
          <p className="flex items-center gap-2 text-lg font-semibold">
            {stillWaiting ? (
              <CloudOff className="size-6" aria-hidden />
            ) : (
              <CircleCheckBig className="size-6" aria-hidden />
            )}
            {stillWaiting
              ? online
                ? 'Sending to the designer...'
                : 'Saved on this device - it will send automatically when you have signal'
              : 'Sent to the designer'}
          </p>
        </div>
      )}

      <WaitingToSend userId={me.real_user_id} />

      {drafts.length > 0 && (
        <section aria-labelledby="drafts" className="flex flex-col gap-3">
          <h2 id="drafts" className="text-xl uppercase">
            Not sent yet
          </h2>
          {drafts.map((d) => (
            <Card key={d.id} className="flex flex-wrap items-center gap-3">
              <FilePen className="size-6" aria-hidden />
              <div className="flex-1">
                <p className="font-semibold">{d.title}</p>
                <p className="text-sm text-fg-muted">
                  Saved on this device {formatWhen(new Date(d.updatedAt))}
                </p>
              </div>
              <Button asChild variant="secondary">
                <Link
                  to="/reluxes/draft/$draftId"
                  params={{ draftId: d.id }}
                  search={{ step: (d.data as { isQuick?: boolean }).isQuick ? 'quick' : undefined }}
                >
                  Continue
                </Link>
              </Button>
            </Card>
          ))}
        </section>
      )}

      {(data?.length ?? 0) > 5 && (
        <Input
          aria-label="Search my Reluxes"
          placeholder="Search by project, ref or suburb"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      )}

      {isLoading && <p className="text-fg-muted">Loading...</p>}
      {!isLoading && (data?.length ?? 0) === 0 && drafts.length === 0 && (
        <EmptyState icon={ClipboardList} title="No Reluxes yet">
          Every Relux you send to The designer will be listed here with its status and ETA.
        </EmptyState>
      )}

      {(
        [
          ['needs', 'Designer needs info', groups.needsYou],
          ['ready', 'Designs ready', groups.ready],
          ['queue', 'In the queue', groups.queue],
          ['done', 'Closed', groups.done],
        ] as const
      ).map(([key, title, list]) =>
        list.length ? (
          <section key={key} aria-labelledby={`g-${key}`} className="flex flex-col gap-3">
            <h2 id={`g-${key}`} className="text-xl uppercase">
              {title} ({list.length})
            </h2>
            {list.map((r) => (
              <ReluxCard key={r.id} r={r} />
            ))}
          </section>
        ) : null,
      )}
    </div>
  )
}
